// Pure helpers for deriving the public URL of an LP Express page and deciding
// when its "open page" link should render. Extracted from
// `client/src/pages/landing-pages-express.tsx` so the derivation has a single
// source of truth AND can be unit-tested without a browser/React render.
//
// Background: the "open page" link after publishing once silently VANISHED
// because the public URL was gated on a brand-slug SNAPSHOT that wasn't present
// on the row. The fix falls back to the live brand slug so the link never
// disappears — the only requirement is that the page is published with a slug.
// `client/src/lib/__tests__/lpExpressPublicUrl.test.ts` locks that in.

// Client-side mirror of the server `lpSlugify` (server/routes.ts). Used to show
// a live preview of how a typed public-URL slug will be normalized, and to
// derive the brand segment of the public URL before the first publish. The
// server re-normalizes on save, so this is for UX only — collisions/validity
// are authoritatively enforced server-side.
export function lpSlugifyClient(input: string): string {
  return (input || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}

type BrandLike = { slug?: string | null; name?: string | null } | null | undefined;

// Brand segment of the public /p/{brandSlug}/{slug} URL. Prefer the snapshot
// stored on the published row so the link stays correct even if the brand's slug
// later changes. If that snapshot is missing/empty (e.g. a legacy row, or before
// the first publish) fall back to the live brand slug so the link never silently
// disappears. Always returns a non-empty string ("brand" as a last resort).
export function lpExpressBrandSlug(
  brand: BrandLike,
  snapshotBrandSlug?: string | null,
): string {
  return (
    snapshotBrandSlug ||
    lpSlugifyClient(brand?.slug || brand?.name || "brand") ||
    "brand"
  );
}

// Public pages live at /p/{brandSlug}/{slug}. Prefer the brand-slug SNAPSHOT
// stored on the row; if it's missing/empty fall back to the live/preview brand
// slug. Returns "" only when there is no slug (i.e. the page isn't published),
// which is the single condition under which the link is intentionally hidden.
export function lpExpressPublicUrl(args: {
  origin: string;
  slug?: string | null;
  rowBrandSlug?: string | null;
  fallbackBrandSlug: string;
}): string {
  const { origin, slug, rowBrandSlug, fallbackBrandSlug } = args;
  if (!slug) return "";
  return `${origin}/p/${rowBrandSlug || fallbackBrandSlug}/${slug}`;
}

// Whether the toolbar "open page" link and the "Copy link" button should render.
// Gated on the page being published AND having a non-empty public URL — never on
// the brand-slug snapshot, which is what caused the link to disappear.
export function lpExpressPublicLinkVisible(
  status: string | null | undefined,
  publicUrl: string,
): boolean {
  return status === "published" && !!publicUrl;
}
